import React, { useEffect, useState } from "react";
import { Button, CustomInput, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster, warningToaster } from "components/common/toast";
import { AddBookingAPI, GetPanelSlotsAPI, SetBookingAPI } from "network/bookings";
import TableSelect from "./TableSelect";
import { errText } from "./errText";
import { bandOfTime, bandsForDay, defaultBand, freeSeats, missingSeats } from "./agendaLogic";

// Provenienze sceglibili a mano, nell'ordine della spec.
const SOURCES = ["phone", "walkin", "web", "qr", "google", "other"];
const EMPTY = { name: "", phone: "", email: "", party_size: 2, time: "", notes: "", allergies: "", source: "phone", table: "", notify: true, whatsapp_consent: false };
const today = () => new Date().toLocaleDateString("sv-SE");
const nowHHMM = () => new Date().toTimeString().slice(0, 5);

// Nuova prenotazione (booking = null) oppure Modifica prenotazione (booking = prenotazione).
const BookingModal = ({ isOpen, booking, date, channels = [], tableGroups = [], sentText = () => "", onClose, onSaved }) => {
  const { t } = useTranslation("bookings");
  const [form, setForm] = useState(EMPTY);
  const [day, setDay] = useState(date);
  const [slots, setSlots] = useState({ shifts: [], closed: false });
  const [bandId, setBandId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const editing = !!booking;

  useEffect(() => {
    if (!isOpen) return;
    setBandId(null);
    setConfirmDelete(false);
    setError("");
    setSlots({ shifts: [], closed: false });
    if (booking) {
      const tables = booking.tables || [];
      setForm({
        ...EMPTY,
        name: booking.name || "", phone: booking.phone || "", email: booking.email || "", party_size: booking.party_size,
        time: booking.time, notes: booking.notes || "", allergies: booking.allergies || "", source: booking.source || "other",
        table: tables.length === 1 ? tables[0].id : "", whatsapp_consent: !!booking.whatsapp_consent, notify: false,
      });
      setDay(booking.date);
    } else {
      setForm(EMPTY);
      setDay(date);
    }
  }, [isOpen, booking, date]);

  useEffect(() => {
    if (!isOpen || !day) return;
    GetPanelSlotsAPI(day, form.party_size || 1, booking && booking.id)
      .then((r) => setSlots({ shifts: [], closed: false, ...r }))
      .catch(() => setSlots({ shifts: [], closed: false }));
  }, [isOpen, day, form.party_size, booking]);

  const bands = bandsForDay(slots.shifts || []);
  // fascia proposta: quella dell'orario (in modifica) o la prossima del giorno
  useEffect(() => {
    if (!bands.length) return;
    setBandId((prev) => {
      if (prev && bands.some((b) => b.id === prev)) return prev;
      const b = bandOfTime(bands, form.time) || (!editing && defaultBand(bands, day === today(), nowHHMM()));
      return b ? b.id : null;
    });
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const band = bands.find((b) => b.id === bandId) || null;
  const missing = missingSeats(form.party_size, band);
  const closedWarn = !!slots.closed || (band && !band.open_today);
  const canSave = !!form.name.trim() && !!form.time && !busy;
  const outside = editing && form.time && band && !band.slots.some((s) => s.time === form.time);
  const multiTables = editing && (booking.tables || []).length > 1;

  const changeDay = (v) => {
    setDay(v);
    set("time", "");
  };
  const changeBand = (id) => {
    setBandId(id);
    set("time", "");
  };

  const save = async () => {
    setError("");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone, email: form.email, party_size: Number(form.party_size) || 1,
        date: day, time: form.time, notes: form.notes, allergies: form.allergies, source: form.source,
        notify: !!form.notify, whatsapp_consent: !!form.whatsapp_consent,
      };
      // più tavoli uniti: restano com'erano finché non si sceglie un altro tavolo
      if (!(multiTables && !form.table)) payload.tables = form.table ? [form.table] : [];
      const res = editing ? await SetBookingAPI(booking.id, payload) : await AddBookingAPI(payload);
      const savedText = t("modal.savedToast", { name: payload.name, count: payload.party_size, time: payload.time });
      const sent = res && res.notified && res.notified.length ? ` ${t("modal.sentToCustomer")}` : "";
      const msg = savedText + sent;
      if (res && res.warning) warningToaster(`${msg} ${res.warning}`);
      else successToaster(msg);
      onSaved(res);
    } catch (e) {
      const text = errText(e);
      setError(text);
      ErrorToaster(text);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await SetBookingAPI(booking.id, { status: "cancelled" });
      setBusy(false);
      const msg = [t("modal.deletedToast", { name: booking.name }), sentText(res && res.notified, "cancel")].filter(Boolean).join(" ");
      successToaster(msg);
      onSaved(res);
    } catch (e) {
      setBusy(false);
      ErrorToaster(errText(e));
    }
  };

  const channelList = channels.map((c) => (c === "email" ? "email" : c === "sms" ? "SMS" : c === "whatsapp" ? "WhatsApp" : c)).join(", ");
  const sources = SOURCES.includes(form.source) ? SOURCES : [form.source, ...SOURCES];
  const cancelled = editing && ["cancelled", "declined"].includes(booking.status);

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>{t(editing ? "modal.edit" : "modal.new")}</ModalHeader>
      <ModalBody>
        <div className="d-flex">
          <div className="mr-2" style={{ flex: 1 }}>
            <Label>{t("modal.date")}</Label>
            <Input type="date" value={day} onChange={(e) => e.target.value && changeDay(e.target.value)} />
          </div>
          <div style={{ width: 100 }}>
            <Label>{t("modal.persons")}</Label>
            <Input type="number" min="1" value={form.party_size} onChange={(e) => set("party_size", e.target.value)} />
          </div>
        </div>

        <Label className="mt-3 d-block">{t("modal.band")}</Label>
        {bands.length === 0 && <div className="small text-danger">{t("modal.noBands")}</div>}
        {bands.map((b) => (
          <Button key={b.id} size="sm" className="mr-1 mb-1" color="primary" outline={bandId !== b.id} onClick={() => changeBand(b.id)}>{b.name}</Button>
        ))}

        {band && (
          <>
            <Label className="mt-3 d-block">{t("modal.time")}</Label>
            <div className="small text-muted mb-1">{t("modal.freeSeats", { free: freeSeats(band), cap: band.capacity, name: band.name.toLowerCase() })}</div>
            <div className="d-flex flex-wrap">
              {band.slots.map((s) => (
                <Button key={s.time} size="sm" className="mr-1 mb-1" color={s.available ? "primary" : "secondary"} outline={form.time !== s.time} onClick={() => set("time", s.time)}>
                  {s.time}
                </Button>
              ))}
            </div>
            {outside && <div className="small text-muted">{t("modal.currentOutside", { time: form.time })}</div>}
          </>
        )}
        {missing > 0 && <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small">{t("modal.missing", { count: missing })}</div>}
        {closedWarn && <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small">{t("modal.closedWarning")}</div>}

        <Label className="mt-3">{t("modal.name")}</Label>
        <Input value={form.name} maxLength={100} onChange={(e) => set("name", e.target.value)} />
        <Label className="mt-2">{t("modal.phone")}</Label>
        <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        <Label className="mt-2">{t("modal.email")}</Label>
        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        <Label className="mt-2">{t("modal.table")}</Label>
        <TableSelect groups={tableGroups} current={editing ? booking.tables || [] : []} value={multiTables && !form.table ? "__multi" : form.table} onChange={(v) => v !== "__multi" && set("table", v)} />
        <Label className="mt-2">{t("modal.allergies")}</Label>
        <Input value={form.allergies} maxLength={200} onChange={(e) => set("allergies", e.target.value)} />
        <Label className="mt-2">{t("modal.source")}</Label>
        <Input type="select" value={form.source} onChange={(e) => set("source", e.target.value)}>
          {sources.map((v) => <option key={v} value={v}>{t(`source.${v}`, { defaultValue: v })}</option>)}
        </Input>
        <Label className="mt-2">{t("modal.notes")}</Label>
        <Input type="textarea" rows="2" maxLength={500} placeholder={t("modal.notesHint")} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        {editing && booking.calls?.length > 0 && (
          <div className="mt-3 p-2" style={{ background: "rgba(184,147,47,.08)", border: "1px solid rgba(184,147,47,.35)", borderRadius: 8 }}>
            <div className="small font-weight-bold mb-1">{t("modal.calls")}</div>
            {booking.calls.map((c) => (
              <div key={c.id} className="small mb-1">
                <span className="text-muted">{c.at.slice(8, 10)}/{c.at.slice(5, 7)} {c.at.slice(11)} · {t(`modal.callOutcome.${c.outcome}`, { defaultValue: c.outcome })}</span>
                <div style={{ whiteSpace: "pre-wrap" }}>{c.summary}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3">
          <CustomInput
            type="switch"
            id="booking-notify"
            checked={!!form.notify}
            onChange={(e) => set("notify", e.target.checked)}
            label={`${t("modal.notify")} ${channelList ? `(${channelList})` : t("modal.noChannels")}`}
          />
          {channels.includes("whatsapp") && (
            <label className="d-block small mt-1 mb-0">
              <input type="checkbox" checked={!!form.whatsapp_consent} onChange={(e) => set("whatsapp_consent", e.target.checked)} /> {t("modal.whatsappConsent")}
            </label>
          )}
        </div>
        {error && <div className="small text-danger mt-2">{error}</div>}
      </ModalBody>
      <ModalFooter className="d-flex">
        {editing && !cancelled && (
          <div className="mr-auto">
            {!confirmDelete ? (
              <Button color="danger" outline size="sm" onClick={() => setConfirmDelete(true)}>{t("modal.delete")}</Button>
            ) : (
              <span className="small">
                {t("modal.deleteConfirm")}{" "}
                <Button color="danger" size="sm" disabled={busy} onClick={remove}>{t("modal.deleteYes")}</Button>{" "}
                <Button size="sm" onClick={() => setConfirmDelete(false)}>{t("modal.deleteNo")}</Button>
              </span>
            )}
          </div>
        )}
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button color="primary" disabled={!canSave} onClick={save}>{t(editing ? "modal.saveEdit" : "modal.saveNew")}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default BookingModal;
